import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { createContext, useCallback, useContext, useMemo, useRef, useState, } from 'react';
import { ReportExportProgressModal } from '@/components/report-export-progress/ReportExportProgressModal';
import { useToastContext } from '@/layout/context/toast-context';
import { fetchAllPagedReportRows, pagedFetchProgressPercent, REPORT_EXPORT_BATCH_SIZE, } from '@/shared/utils/paged-report-export';
const EMPTY_PROGRESS = {
    fetchedCount: 0,
    totalCount: 0,
    completedBatches: 0,
    totalBatches: 0,
};
const ReportExportContext = createContext(null);
export function ReportExportProvider({ children }) {
    const { showToast } = useToastContext();
    const [visible, setVisible] = useState(false);
    const [isExporting, setIsExporting] = useState(false);
    const [title, setTitle] = useState('Exporting report');
    const [status, setStatus] = useState('idle');
    const [progress, setProgress] = useState(0);
    const [statusMessage, setStatusMessage] = useState('');
    const [progressDetail, setProgressDetail] = useState(EMPTY_PROGRESS);
    const [estimatedRemainingMs, setEstimatedRemainingMs] = useState(null);
    const exportInFlightRef = useRef(false);
    const startedAtRef = useRef(null);
    const abortControllerRef = useRef(null);
    const resetModal = useCallback(() => {
        setVisible(false);
        setStatus('idle');
        setProgress(0);
        setStatusMessage('');
        setProgressDetail(EMPTY_PROGRESS);
        setEstimatedRemainingMs(null);
    }, []);
    const minimizeModal = useCallback(() => {
        setVisible(false);
    }, []);
    const cancelExport = useCallback(() => {
        abortControllerRef.current?.abort();
    }, []);
    const closeModal = useCallback(() => {
        if (exportInFlightRef.current) {
            minimizeModal();
            return;
        }
        resetModal();
    }, [minimizeModal, resetModal]);
    const updateProgress = useCallback((next) => {
        setProgressDetail(next);
        setProgress(pagedFetchProgressPercent(next));
        if (next.totalCount <= 0) {
            setStatusMessage('No records to export.');
            setEstimatedRemainingMs(null);
            return;
        }
        setStatusMessage(`Downloaded ${next.fetchedCount.toLocaleString()} of ${next.totalCount.toLocaleString()} records (batch ${next.completedBatches} of ${next.totalBatches}).`);
        if (next.fetchedCount > 0 && startedAtRef.current != null) {
            const elapsed = Date.now() - startedAtRef.current;
            const remaining = Math.round((elapsed / next.fetchedCount) * (next.totalCount - next.fetchedCount));
            setEstimatedRemainingMs(Number.isFinite(remaining) ? remaining : null);
        }
    }, []);
    const runExport = useCallback(async (options) => {
        if (exportInFlightRef.current)
            return;
        exportInFlightRef.current = true;
        setIsExporting(true);
        setVisible(true);
        setTitle(options.title);
        setStatus('fetching');
        setProgress(0);
        setStatusMessage('Preparing export…');
        setProgressDetail(EMPTY_PROGRESS);
        setEstimatedRemainingMs(null);
        startedAtRef.current = Date.now();
        const abortController = new AbortController();
        abortControllerRef.current = abortController;
        try {
            const result = await fetchAllPagedReportRows({
                fetchPage: options.fetchPage,
                query: options.query,
                batchSize: options.batchSize ?? REPORT_EXPORT_BATCH_SIZE,
                onProgress: updateProgress,
                signal: abortController.signal,
            });
            if (result.rows.length === 0) {
                setStatus('error');
                setStatusMessage(options.emptyDetail ?? 'No records matched the current filters.');
                setVisible(true);
                showToast({
                    severity: 'info',
                    summary: 'Export',
                    detail: options.emptyDetail ?? 'No records matched the current filters.',
                });
                return;
            }
            setStatus('generating');
            setProgress(100);
            setStatusMessage('Generating CSV file…');
            setEstimatedRemainingMs(null);
            options.onExport(result.rows);
            setStatus('success');
            setStatusMessage(options.successDetail?.(result.rows.length) ??
                `Successfully exported ${result.rows.length.toLocaleString()} records.`);
            setVisible(true);
            showToast({
                severity: 'success',
                summary: 'Export complete',
                detail: options.successDetail?.(result.rows.length) ??
                    `${result.rows.length.toLocaleString()} records exported.`,
            });
        }
        catch (error) {
            if (error instanceof DOMException && error.name === 'AbortError') {
                setStatus('cancelled');
                setStatusMessage('Export cancelled.');
                setEstimatedRemainingMs(null);
                setVisible(true);
                showToast({
                    severity: 'info',
                    summary: 'Export cancelled',
                    detail: 'The export was stopped.',
                });
                return;
            }
            setStatus('error');
            setStatusMessage(options.errorDetail ?? 'Could not download all report rows. Try narrowing your filters.');
            setVisible(true);
            showToast({
                severity: 'error',
                summary: 'Export failed',
                detail: options.errorDetail ?? 'Could not download all report rows. Try narrowing your filters.',
            });
        }
        finally {
            abortControllerRef.current = null;
            exportInFlightRef.current = false;
            setIsExporting(false);
            startedAtRef.current = null;
        }
    }, [showToast, updateProgress]);
    const value = useMemo(() => ({
        isExporting,
        runExport,
        cancelExport,
        closeModal,
        minimizeModal,
    }), [cancelExport, closeModal, isExporting, minimizeModal, runExport]);
    return (_jsxs(ReportExportContext.Provider, { value: value, children: [children, _jsx(ReportExportProgressModal, { visible: visible, title: title, status: status, progress: progress, statusMessage: statusMessage, fetchedCount: progressDetail.fetchedCount, totalCount: progressDetail.totalCount, completedBatches: progressDetail.completedBatches, totalBatches: progressDetail.totalBatches, estimatedRemainingMs: estimatedRemainingMs, onHide: closeModal, onMinimize: minimizeModal, onCancel: cancelExport })] }));
}
export function useBatchedReportExport() {
    const context = useContext(ReportExportContext);
    if (!context) {
        throw new Error('useBatchedReportExport must be used within ReportExportProvider');
    }
    return context;
}
